import { useEffect, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { usePeopleSearch,
  useAvatarUrlResolver,
  MENSAJE_MAX_LENGTH,
} from '@/api/recognition';
import type { PersonSummary } from '@/api/recognition';
import { EmojiPickerButton } from '@/components/common/EmojiPickerButton';
import { insertAtCursor } from '@/utils/insertAtCursor';
import { useAutoGrowTextarea } from '@/hooks/useAutoGrowTextarea';
import { MessageLimitHint } from './messageLimit';
import { DRAG_MIME, PersonAvatar } from './postitShared';
import type { BankTab } from './WordBankTabs';

export type SlotId = 'persona' | 'accion' | 'comportamiento' | 'habito' | 'impacto';

/** MIME de arrastre que acepta cada slot de la frase — 'persona' no entra: ese destinatario se
 *  elige por búsqueda (`RecipientSlot`), no arrastrándolo desde el banco de palabras. */
const SLOT_DRAG_MIME: Partial<Record<SlotId, string>> = {
  accion: DRAG_MIME.fundamental,
  comportamiento: DRAG_MIME.behavior,
  habito: DRAG_MIME.habit,
  impacto: DRAG_MIME.impact,
};

function useDebounced(value: string, delay = 250): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeoutId = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timeoutId);
  }, [value, delay]);
  return debounced;
}

interface TemplateSlotProps {
  slotId: SlotId;
  filled: boolean;
  hoveredSlot: SlotId | null;
  onHoverSlot: (slot: SlotId | null) => void;
  onRemove: () => void;
  /** Vacío + click: salta al banco de palabras (pestaña correspondiente) y lo hace scrollIntoView —
   *  el usuario suele probar clickear aquí primero (el patrón visual de "llenar el espacio en
   *  blanco" lo invita), y antes no pasaba nada; ahora lo lleva directo a donde sí puede elegir. */
  onEmptyClick: () => void;
  /** Palabra arrastrada desde el banco (`WordBankTabs`) y soltada aquí — mismo efecto que
   *  elegirla ahí: solo se invoca sobre un slot vacío y de su propia categoría (ver `SLOT_DRAG_MIME`). */
  onDropValue: (raw: string) => void;
  clickHint: string;
  clickToRemove: string;
  placeholder: string;
  children: ReactNode;
}

/** Slot genérico de la frase (fundamental/comportamiento/hábito/impacto): lleno muestra la
 *  palabra elegida y permite quitarla con un click; vacío muestra el placeholder, se resalta al
 *  pasar el mouse (o al arrastrar una palabra compatible por encima) para guiar al banco de
 *  palabras, y un click salta directo a esa pestaña — soltar una palabra arrastrada la elige. */
function TemplateSlot({
  slotId, filled, hoveredSlot, onHoverSlot, onRemove, onEmptyClick, onDropValue,
  clickHint, clickToRemove, placeholder, children,
}: Readonly<TemplateSlotProps>) {
  const dragMime = SLOT_DRAG_MIME[slotId];
  const handleEnter = () => {
    if (!filled) onHoverSlot(slotId);
  };
  const handleClick = () => {
    if (filled) onRemove();
    else onEmptyClick();
  };
  const acceptsDrag = (event: DragEvent<HTMLSpanElement>) => !filled && !!dragMime && event.dataTransfer.types.includes(dragMime);
  const handleDragOver = (event: DragEvent<HTMLSpanElement>) => {
    if (!acceptsDrag(event)) return;
    event.preventDefault();
    onHoverSlot(slotId);
  };
  const handleDrop = (event: DragEvent<HTMLSpanElement>) => {
    if (!acceptsDrag(event) || !dragMime) return;
    event.preventDefault();
    onHoverSlot(null);
    const raw = event.dataTransfer.getData(dragMime);
    if (raw) onDropValue(raw);
  };
  return (
    <span
      className={`ri-slot ${filled ? 'ri-filled' : ''} ${hoveredSlot === slotId ? 'ri-hover' : ''}`}
      onMouseEnter={handleEnter}
      onMouseLeave={() => onHoverSlot(null)}
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={() => { if (!filled) onHoverSlot(null); }}
      onDrop={handleDrop}
      title={filled ? clickToRemove : clickHint}
    >
      {filled ? children : placeholder}
    </span>
  );
}

interface RecipientSlotProps {
  recipient: PersonSummary | null;
  onPickRecipient: (person: PersonSummary) => void;
  onRemoveRecipient: () => void;
  excludeIds: string[];
  hovered: boolean;
  onHoverEnter: () => void;
  onHoverLeave: () => void;
  clickHint: string;
}

/** Slot del destinatario: chip con el nombre si ya hay uno elegido, o un input con búsqueda
 *  con debounce y dropdown de sugerencias (excluye a quienes ya están en `excludeIds`). */
function RecipientSlot({
  recipient, onPickRecipient, onRemoveRecipient, excludeIds, hovered, onHoverEnter, onHoverLeave, clickHint,
}: Readonly<RecipientSlotProps>) {
  // RF-008 - se resuelve id -> url UNA vez para toda la lista: `useAvatarUrl` es un hook y no se
  // puede llamar dentro del `.map()`. Comparte cache con el resto, asi que no hay peticion extra.
  const urlDeAvatar = useAvatarUrlResolver();
  const { t } = useTranslation('recognition');
  const tagRef = useRef<HTMLDivElement>(null);
  const [tagQuery, setTagQuery] = useState('');
  const [showTagDrop, setShowTagDrop] = useState(false);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (tagRef.current && !tagRef.current.contains(event.target as Node)) setShowTagDrop(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const tagSearch = useDebounced(tagQuery);
  const tagResults = usePeopleSearch({ search: tagSearch, pageSize: 5 });
  const suggestions = (tagResults.data?.items ?? []).filter((person) => !excludeIds.includes(person.id));
  useEffect(() => {
    setShowTagDrop(suggestions.length > 0 && tagQuery.trim().length > 0);
  }, [suggestions.length, tagQuery]);

  const recipientLabel = recipient ? (recipient.displayName ?? recipient.email) : null;

  const pickPerson = (person: PersonSummary) => {
    onPickRecipient(person);
    setTagQuery('');
    setShowTagDrop(false);
  };

  return (
    <span
      className={`ri-slot ${recipient ? 'ri-filled' : ''} ${hovered ? 'ri-hover' : ''}`}
      onMouseEnter={() => { if (!recipient) onHoverEnter(); }}
      onMouseLeave={onHoverLeave}
      title={clickHint}
    >
      {recipient ? (
        <span className="ri-word ri-word--persona">
          <PersonAvatar name={recipientLabel ?? ''} photoUrl={recipient.photoUrl ?? urlDeAvatar(recipient.avatar)} baseClass="ri-word-persona-av" />
          @{recipientLabel}
          <button type="button" className="ri-word-remove" onClick={onRemoveRecipient} aria-label={t('common.remove')}>×</button>
        </span>
      ) : (
        <span className="ri-tag-input-wrap" ref={tagRef}>
          <span className="ri-at">@</span>
          <input
            className="ri-tag-input"
            value={tagQuery}
            onChange={(event) => setTagQuery(event.target.value)}
            onFocus={() => suggestions.length > 0 && setShowTagDrop(true)}
            placeholder={t('ri.searchPlaceholder')}
          />
          {showTagDrop && (
            <div className="ri-tag-dropdown">
              {suggestions.map((person, indice) => (
                <button key={person.id} type="button" className="ri-tag-option" onMouseDown={() => pickPerson(person)}>
                  <PersonAvatar
                    name={person.displayName ?? person.email}
                    photoUrl={person.photoUrl ?? urlDeAvatar(person.avatar)}
                    baseClass="ri-tag-option-av"
                    colorIndex={indice}
                  />
                  <span>
                    <span className="ri-tag-option-name">{person.displayName ?? person.email}</span>
                    {person.jobTitle && <span className="ri-tag-option-role">{person.jobTitle}</span>}
                  </span>
                </button>
              ))}
            </div>
          )}
        </span>
      )}
    </span>
  );
}

export interface PostItCardProps {
  recipient: PersonSummary | null;
  onPickRecipient: (person: PersonSummary) => void;
  onRemoveRecipient: () => void;
  excludeIds: string[];

  fundamentalId: string | null;
  fundLabel: string | null;
  fundColor: string | null;
  onRemoveFundamental: () => void;
  onDropFundamental: (id: string) => void;

  behaviorRequired: boolean;
  behaviorId: string | null;
  behaviorLabel: string | null;
  onRemoveBehavior: () => void;
  onDropBehavior: (id: string) => void;

  habitRequired: boolean;
  habitId: string | null;
  habitLabel: string | null;
  onRemoveHabit: () => void;
  onDropHabit: (id: string) => void;

  impactIdx: number | null;
  impactLabel: string | null;
  onRemoveImpact: () => void;
  onDropImpact: (idx: number) => void;

  hoveredSlot: SlotId | null;
  onHoverSlot: (slot: SlotId | null) => void;
  /** Click en un slot vacío del post-it: salta a la pestaña del banco que le corresponde. */
  onJumpToTab: (tab: BankTab) => void;

  message: string;
  onMessageChange: (value: string) => void;
  anonymous: boolean;
  onAnonymousChange: (value: boolean) => void;
  /** Si `false`, el checkbox "Enviar como anónimo" no se pinta (Gestión/campaña lo tienen apagado). */
  showAnonymous?: boolean;
  /** T5 — solo se ofrece el check "enviar también a Brillamos" si Gestión lo permite y hay una
   *  campaña Brillamos próxima programada (mismo criterio que ReconocerPanel, formulario clásico). */
  showSendToBrillamos: boolean;
  sendToBrillamos: boolean;
  onSendToBrillamosChange: (value: boolean) => void;

  /** R7 — kill-switch/excepción de localidad `DesactivarEnvio`: bloquea el envío y muestra el
   *  aviso, mismo criterio que `ReconocerPanel`. `canSend` ya lo incorpora; este prop es solo
   *  para decidir el aviso. */
  sendingDisabled?: boolean;
  /** R7 — excepción de localidad `HorarioRestringido` activa AHORA MISMO: bloquea el envío y
   *  muestra el aviso (mismo criterio que `sendingDisabled`, sin duplicar el mensaje si ambos aplican). */
  scheduleRestricted?: boolean;
  /** R7 — tope EFECTIVO de envíos por período de la localidad, ya resuelto por el backend. Solo
   *  informativo: `null`/ausente = sin ningún tope vigente que anunciar. */
  weeklySendLimitEffective?: number | null;

  canSend: boolean;
  isSending: boolean;
  sent: boolean;
  onSend: () => void;
  onReset: () => void;

  /**
   * Mismo criterio de mínimo de caracteres que el reconocimiento clásico (`ReconocerPanel`):
   * `messageLength`/`messageOk` se miden SOLO contra lo que el usuario escribe en la caja libre,
   * no contra la frase que arma el post-it — la plantilla no cuenta como texto propio.
   */
  messageLength: number;
  messageOk: boolean;
  minChars: number;

  /**
   * Largo del mensaje REAL que se enviará: la frase armada con el banco de palabras MÁS lo escrito
   * en la caja libre. El tope de caracteres se mide contra esto —es lo que viaja al backend—, a
   * diferencia del mínimo, que sigue mirando solo el texto propio del usuario.
   */
  combinedLength: number;
}

/** Post-it central del Word Builder: frase con slots + personalización + envío (REQ-104). */
export function PostItCard({
  recipient, onPickRecipient, onRemoveRecipient, excludeIds,
  fundamentalId, fundLabel, fundColor, onRemoveFundamental, onDropFundamental,
  behaviorRequired, behaviorId, behaviorLabel, onRemoveBehavior, onDropBehavior,
  habitRequired, habitId, habitLabel, onRemoveHabit, onDropHabit,
  impactIdx, impactLabel, onRemoveImpact, onDropImpact,
  hoveredSlot, onHoverSlot, onJumpToTab,
  message, onMessageChange, anonymous, onAnonymousChange, showAnonymous = true,
  showSendToBrillamos, sendToBrillamos, onSendToBrillamosChange,
  sendingDisabled = false, scheduleRestricted = false, weeklySendLimitEffective = null,
  canSend, isSending, sent, onSend, onReset,
  messageLength, messageOk, minChars, combinedLength,
}: Readonly<PostItCardProps>) {
  const { t } = useTranslation('recognition');
  const messageRef = useRef<HTMLTextAreaElement>(null);
  // Espacio que queda para la caja libre: el tope es del mensaje completo, así que la frase del
  // post-it ya consumió su parte. `Math.max` evita un maxLength negativo si la frase creciera.
  const freeTextMaxLength = Math.max(0, MENSAJE_MAX_LENGTH - (combinedLength - message.length));
  const [nudgeVisible, setNudgeVisible] = useState(false);
  // Igual que el formulario clásico: 4 líneas de piso, 10 de techo y scroll a partir de ahí. Los
  // topes están en `.msg-autogrow` (recognition.css); el hook solo escribe la altura medida.
  useAutoGrowTextarea(messageRef, message);
  const clickHint = t('ri.clickHint');
  const clickToRemove = t('ri.clickToRemove');

  const handleSendClick = () => {
    if (!message.trim()) {
      setNudgeVisible(true);
      return;
    }
    onSend();
  };

  if (sent) {
    return (
      <div className="ri-postit-wrap">
        <div className="ri-postit ri-postit--sent">
          <span className="ri-emoji-celebrate">🎉</span>
          <p>{t('ri.sent')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ri-postit-wrap">
      <div className="ri-postit">
        <div className="ri-sentence">
          <span>{t('ri.templateThanks')}</span>
          <RecipientSlot
            recipient={recipient}
            onPickRecipient={onPickRecipient}
            onRemoveRecipient={onRemoveRecipient}
            excludeIds={excludeIds}
            hovered={hoveredSlot === 'persona'}
            onHoverEnter={() => onHoverSlot('persona')}
            onHoverLeave={() => onHoverSlot(null)}
            clickHint={clickHint}
          />
          <span>{t('ri.templateForLiving')}</span>
          <TemplateSlot
            slotId="accion"
            filled={!!fundamentalId}
            hoveredSlot={hoveredSlot}
            onHoverSlot={onHoverSlot}
            onRemove={onRemoveFundamental}
            onEmptyClick={() => onJumpToTab('fundamentales')}
            onDropValue={onDropFundamental}
            clickHint={clickHint}
            clickToRemove={clickToRemove}
            placeholder={t('ri.slotAccion')}
          >
            <span className="ri-word" style={{ background: fundColor ?? undefined, borderColor: fundColor ?? undefined, color: '#fff' }}>
              {fundLabel}
            </span>
          </TemplateSlot>
          {behaviorRequired && (
            <>
              <span>{t('ri.templateWith')}</span>
              <TemplateSlot
                slotId="comportamiento"
                filled={!!behaviorId}
                hoveredSlot={hoveredSlot}
                onHoverSlot={onHoverSlot}
                onRemove={onRemoveBehavior}
                onEmptyClick={() => onJumpToTab('comportamientos')}
                onDropValue={onDropBehavior}
                clickHint={clickHint}
                clickToRemove={clickToRemove}
                placeholder={t('ri.slotComportamiento')}
              >
                <span
                  className="ri-word ri-word--comportamiento"
                  style={fundColor ? { background: `${fundColor}18`, borderColor: fundColor, color: fundColor } : undefined}
                >
                  {behaviorLabel}
                </span>
              </TemplateSlot>
            </>
          )}
          {habitRequired && (
            <>
              <span>{t('ri.templateShowing')}</span>
              <TemplateSlot
                slotId="habito"
                filled={!!habitId}
                hoveredSlot={hoveredSlot}
                onHoverSlot={onHoverSlot}
                onRemove={onRemoveHabit}
                onEmptyClick={() => onJumpToTab('habitos')}
                onDropValue={onDropHabit}
                clickHint={clickHint}
                clickToRemove={clickToRemove}
                placeholder={t('ri.slotHabito')}
              >
                <span
                  className="ri-word ri-word--habito"
                  style={fundColor ? { background: `${fundColor}18`, borderColor: fundColor, color: fundColor } : undefined}
                >
                  {habitLabel}
                </span>
              </TemplateSlot>
            </>
          )}
          <span>{t('ri.templateThat')}</span>
          <TemplateSlot
            slotId="impacto"
            filled={impactIdx !== null}
            hoveredSlot={hoveredSlot}
            onHoverSlot={onHoverSlot}
            onRemove={onRemoveImpact}
            onEmptyClick={() => onJumpToTab('impacto')}
            onDropValue={(raw) => onDropImpact(Number(raw))}
            clickHint={clickHint}
            clickToRemove={clickToRemove}
            placeholder={t('ri.slotImpacto')}
          >
            <span className={`ri-word ri-word--impacto-${impactIdx}`}>{impactLabel}</span>
          </TemplateSlot>
          <span>.</span>
        </div>

        <div className="ri-postit-personal">
          <div className="ri-postit-personal-head">
            <label className="ri-postit-personal-label" htmlFor="ri-personal-message">{t('ri.msgLabel')}</label>
            <EmojiPickerButton
              onSelect={(emoji) => {
                const { next, cursor } = insertAtCursor(messageRef.current, message, emoji, freeTextMaxLength);
                onMessageChange(next);
                requestAnimationFrame(() => {
                  messageRef.current?.focus();
                  messageRef.current?.setSelectionRange(cursor, cursor);
                });
              }}
            />
          </div>
          <textarea
            id="ri-personal-message"
            ref={messageRef}
            className="ri-postit-textarea msg-autogrow"
            placeholder={t('builder.complementPlaceholder')}
            value={message}
            onChange={(event) => { onMessageChange(event.target.value); setNudgeVisible(false); }}
            rows={4}
            maxLength={freeTextMaxLength}
          />
          {/* El aviso mide el mensaje COMPLETO (frase + caja) y lo dice, porque si no el conteo no
              cuadraría con lo que el usuario ve tecleado. */}
          <MessageLimitHint length={combinedLength} hasBuilderSentence />
          {showAnonymous && (
            <label className="rp-anon">
              <input type="checkbox" checked={anonymous} onChange={(event) => onAnonymousChange(event.target.checked)} />
              {t('panel.anonymous')}
            </label>
          )}
          {showSendToBrillamos && (
            <label className="rp-anon rp-anon--brillamos">
              <input
                type="checkbox"
                checked={sendToBrillamos}
                onChange={(event) => onSendToBrillamosChange(event.target.checked)}
              />
              {t('panel.sendToBrillamos')}
            </label>
          )}
          {/* R7 — mismo tratamiento que ReconocerPanel: kill-switch/excepción de horario bloquean el
              envío (con aviso, sin duplicar mensaje), el tope efectivo es solo informativo. */}
          {sendingDisabled && (
            <p className="rp-error" role="alert">
              <i className="fas fa-circle-info" /> {t('panel.sendingDisabledHint')}
            </p>
          )}
          {!sendingDisabled && scheduleRestricted && (
            <p className="rp-error" role="alert">
              <i className="fas fa-circle-info" /> {t('panel.scheduleRestrictedHint')}
            </p>
          )}
          {weeklySendLimitEffective != null && (
            <p className="rp-note">{t('panel.weeklyLimitHint', { count: weeklySendLimitEffective })}</p>
          )}
        </div>

        {nudgeVisible && (
          <div className="ri-nudge">
            <p>{t('ri.nudgeMsg')}</p>
            <div className="ri-nudge-btns">
              <button type="button" className="ri-nudge-add" onClick={() => { setNudgeVisible(false); messageRef.current?.focus(); }}>
                {t('ri.nudgeAdd')}
              </button>
              <button type="button" className="ri-nudge-skip" onClick={() => { setNudgeVisible(false); onSend(); }}>
                {t('ri.nudgeSkip')}
              </button>
            </div>
          </div>
        )}

        <div className="ri-postit-footer">
          <div className="ri-postit-footer-actions">
            <span className="rp-char-count">{t('panel.charCount', { count: messageLength })}</span>
            <button type="button" className="ri-reset-btn" onClick={onReset}>{t('ri.clear')}</button>
            <button type="button" className="ri-send-btn" disabled={!canSend || isSending} onClick={handleSendClick}>
              {isSending ? t('panel.sending') : t('panel.send')}
            </button>
          </div>
          {!messageOk && (
            <ul className="rp-missing-list" role="status">
              <li>{t('panel.messageMinCharsHint', { count: minChars })}</li>
            </ul>
          )}
        </div>
        <div className="ri-quote">{t('ri.quote')}</div>
      </div>
    </div>
  );
}

export default PostItCard;
